/**
 * Overview Section Header Component
 * Displays section title, match score, and action buttons (Save, Menu, Hide, Share)
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { BookmarkSimple, EyeSlash, ShareNetwork, DotsThreeVertical } from '@phosphor-icons/react';
import { MatchDonutWithLoginPrompt } from '../../../../../components/MatchDonut';
import { EllipsesMenuDropdown } from './EllipsesMenuDropdown';
import { isDark } from '../utils/jobDetailUtils';
import {
  JD_ICON_BTN_BASE,
  JD_ICON_BTN_GHOST,
  JD_ICON_MUTED,
  JD_SAVE_FILLED_THEME,
  JD_SAVE_ICON_THEME,
  applicationStateChrome,
} from '../utils/job-detail-action-chrome';
import { JD_MORE_INFO_ICON_BUTTON_HOVER } from '../../../../../utils/job-detail/job-detail-more-info-affordance';
import type { DisplayJob } from '../../../../../types';
import type { ApplicationState } from '../../../../../types/tracked-job';
import type { SearchInferenceBullet } from '../../../../../types/job-match';
import type { MatchScoreBreakdown } from '../../../../../utils/data/calculate-job-match-score';
import { useFormatMessage } from '../../../../../i18n-review/client/Msg';

export interface OverviewSectionHeaderProps {
  displayJob: DisplayJob | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  matchScoreData: MatchScoreBreakdown | null;
  matchScoreMeta?: { source: 'recent-searches'; bullets: SearchInferenceBullet[] } | null;
  onLoginClick: () => void;
  isSaved: boolean;
  isApplied: boolean;
  isHidden: boolean;
  currentApplicationState: ApplicationState | null;
  ellipsisButtonState: {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  } | null;
  isMenuOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  effectiveBrandColour: string;
  useBrandColor: boolean;
  onSave: () => Promise<void>;
  onToggleHide: () => void;
  onShare: () => void;
  onSetApplicationState: (state: ApplicationState | null) => void;
  onToggleApplied: () => void;
  onMenuToggle: () => void;
  /** V2 redesign: ring stroke uses the company's brand panel color instead of the score-based color. */
  ringColorOverride?: string;
  /** V2 redesign: center score digit uses a fixed ink color instead of following the ring color. */
  labelColorOverride?: string;
}

export const OverviewSectionHeader: React.FC<OverviewSectionHeaderProps> = ({
  displayJob,
  isAuthenticated,
  isHydrated,
  matchScoreData,
  matchScoreMeta = null,
  onLoginClick,
  isSaved,
  isApplied,
  isHidden,
  currentApplicationState,
  ellipsisButtonState,
  isMenuOpen,
  menuRef,
  effectiveBrandColour,
  useBrandColor,
  onSave,
  onToggleHide,
  onShare,
  onSetApplicationState,
  onToggleApplied,
  onMenuToggle,
  ringColorOverride,
  labelColorOverride,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const matchScore = matchScoreData?.overall ?? null;

  const getSaveButtonStyle = () => {
    if (!isSaved) return {};
    return {
      backgroundColor: useBrandColor ? effectiveBrandColour : '#bbf7d0',
      borderColor: useBrandColor ? effectiveBrandColour : '#bbf7d0',
    };
  };

  const getSaveButtonHoverStyle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (useBrandColor) {
      e.currentTarget.style.backgroundColor = effectiveBrandColour;
      e.currentTarget.style.borderColor = effectiveBrandColour;
      e.currentTarget.style.filter = 'brightness(0.92)';
    } else {
      e.currentTarget.style.backgroundColor = '#6ee7b7';
      e.currentTarget.style.borderColor = '#6ee7b7';
    }
  };

  const getSaveButtonOutStyle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (useBrandColor) {
      e.currentTarget.style.backgroundColor = effectiveBrandColour;
      e.currentTarget.style.borderColor = effectiveBrandColour;
      e.currentTarget.style.filter = '';
    } else {
      e.currentTarget.style.backgroundColor = '#bbf7d0';
      e.currentTarget.style.borderColor = '#bbf7d0';
    }
  };

  return (
    <div className="flex w-full lg:w-auto max-w-full flex-wrap items-center justify-end gap-2">
      {/* Match Score Donut */}
      {displayJob && isHydrated && (
        <MatchDonutWithLoginPrompt
          score={matchScore}
          size={44}
          onLoginClick={onLoginClick}
          breakdown={matchScoreData ?? undefined}
          isAuthenticated={isAuthenticated}
          matchScoreMeta={matchScoreMeta}
          ringColorOverride={ringColorOverride}
          labelColorOverride={labelColorOverride}
        />
      )}

      {/* Save Button */}
      <button
        type="button"
        className={
          isSaved && useBrandColor
            ? `border rounded-full w-11 h-11 flex items-center justify-center shadow-sm transition-all duration-200 cursor-pointer ${JD_MORE_INFO_ICON_BUTTON_HOVER}`
            : isSaved
              ? `${JD_SAVE_FILLED_THEME} ${JD_MORE_INFO_ICON_BUTTON_HOVER}`
              : `${JD_ICON_BTN_GHOST} ${JD_MORE_INFO_ICON_BUTTON_HOVER}`
        }
        aria-label={
          isSaved
            ? formatMessage({
                id: 'hit.saved.detail',
                defaultMessage: 'Saved',
                description: 'Button label when job is saved on detail page'
              })
            : formatMessage({
                id: 'hit.save.detail',
                defaultMessage: 'Save',
                description: 'Button label to save a job on detail page'
              })
        }
        style={isSaved && useBrandColor ? getSaveButtonStyle() : undefined}
        onMouseOver={isSaved && useBrandColor ? getSaveButtonHoverStyle : undefined}
        onMouseOut={isSaved && useBrandColor ? getSaveButtonOutStyle : undefined}
        onClick={onSave}
      >
        {isSaved ? (
          <BookmarkSimple
            weight="fill"
            className={`h-5 w-5 ${useBrandColor ? '' : JD_SAVE_ICON_THEME}`}
            style={
              useBrandColor ? { color: isDark(effectiveBrandColour) ? '#fff' : '#111827' } : undefined
            }
          />
        ) : (
          <BookmarkSimple className={`h-5 w-5 ${JD_ICON_MUTED}`} />
        )}
      </button>

      {/* Ellipses Menu */}
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          className={`${
            ellipsisButtonState
              ? `${applicationStateChrome(ellipsisButtonState.color).surface} ${JD_ICON_BTN_BASE} hover:brightness-[0.98] hover:scale-105 active:scale-95 cursor-pointer ${JD_MORE_INFO_ICON_BUTTON_HOVER}`
              : `${JD_ICON_BTN_GHOST} ${JD_MORE_INFO_ICON_BUTTON_HOVER}`
          }`}
          aria-label={
            ellipsisButtonState
              ? ellipsisButtonState.label
              : formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options', description: 'Button label for menu with additional job actions (save, hide, mark as applied, etc.)' })
          }
          onClick={onMenuToggle}
        >
          {ellipsisButtonState ? (() => {
            const IconComponent = ellipsisButtonState.icon;
            const { icon } = applicationStateChrome(ellipsisButtonState.color);
            return <IconComponent className={`h-5 w-5 ${icon}`} />;
          })() : (
            <DotsThreeVertical className={`h-5 w-5 ${JD_ICON_MUTED}`} />
          )}
        </button>

        <EllipsesMenuDropdown
          isOpen={isMenuOpen}
          currentApplicationState={currentApplicationState}
          isApplied={isApplied}
          isHidden={isHidden}
          onSetApplicationState={onSetApplicationState}
          onToggleApplied={onToggleApplied}
          onToggleHide={onToggleHide}
          onClose={onMenuToggle}
        />
      </div>

      {/* Hide Button */}
      <button
        type="button"
        className={
          isHidden
            ? `${JD_ICON_BTN_BASE} bg-jc-muted border-jc-muted cursor-pointer ${JD_MORE_INFO_ICON_BUTTON_HOVER}`
            : `${JD_ICON_BTN_GHOST} ${JD_MORE_INFO_ICON_BUTTON_HOVER}`
        }
        aria-label={
          isHidden
            ? formatMessage({ id: 'hit.hidden', defaultMessage: 'Hidden', description: 'Button label when a job is currently hidden from search results' })
            : formatMessage({ id: 'hit.hide.button', defaultMessage: 'Hide', description: 'Button label to hide a job' })
        }
        onClick={onToggleHide}
      >
        <EyeSlash className={`h-5 w-5 ${isHidden ? 'text-jc-surface' : JD_ICON_MUTED}`} />
      </button>

      {/* Share Button */}
      <button
        type="button"
        className={`${JD_ICON_BTN_GHOST} ${JD_MORE_INFO_ICON_BUTTON_HOVER}`}
        aria-label={formatMessage({
          id: 'hit.share.detail',
          defaultMessage: 'Share',
          description: 'Button label to share a job on detail page'
        })}
        onClick={onShare}
      >
        <ShareNetwork className={`h-5 w-5 ${JD_ICON_MUTED}`} />
      </button>
    </div>
  );
};
